<template>
  <section class="tl" ref="sectionRoot">
    <template v-for="(e, i) in filteredEntries" :key="i">
      <!-- RICH entry: has photo, video, or fact, OR has long body -->
      <article
        v-if="isRich(e)"
        :ref="(el: any) => { if (el) entryRefs[i] = el as HTMLElement; }"
        :data-coords="e.coords ? `${e.coords[0]},${e.coords[1]}` : ''"
        :data-year="formatYear(e)"
        :data-place="e.placeName || ''"
        :data-idx="i"
        :class="['tle', 'tle--rich', i % 2 === 1 ? 'tle--alt' : '']"
      >
        <div class="tle__inner">
          <header class="tle__head">
            <div class="tle__year">{{ formatYear(e) }}</div>
          </header>
          <h3 class="tle__title">{{ e.title }}</h3>

          <div class="tle__layout" :class="{ 'no-side': !hasSide(e) }">
            <div class="tle__body" v-html="e.body"></div>
            <aside v-if="hasSide(e)" class="tle__side">
              <figure v-if="e.img" class="tle__fig">
                <img
                  :src="e.img"
                  :alt="e.imgAlt || ''"
                  :class="['tle__fig-img', `tle__fig-img--${e.imgFit || 'cover'}`]"
                />
                <figcaption v-html="e.imgCap || ''"></figcaption>
              </figure>
              <div v-else-if="e.videoId" class="tle__video">
                <VideoEmbed :videoId="e.videoId" />
              </div>
              <div v-if="e.fact" class="tle__fact">
                <div class="tle__factnum">{{ e.fact.num }}<span v-if="e.fact.unit" class="tle__factunit">{{ e.fact.unit }}</span></div>
                <div class="tle__factlabel">{{ e.fact.label }}</div>
              </div>
            </aside>
          </div>

          <!-- Embedded chart blocks (rich infographics per hero year) -->
          <div v-for="embedName in e.embeds || []" :key="embedName" class="tle__embed">
            <component :is="embedMap[embedName]" />
          </div>

          <!-- Inline pull-quote, attached to a specific year -->
          <figure v-if="e.quote" class="tle__quote">
            <blockquote v-html="highlightedQuote(e.quote.text, e.quote.emphasis)" />
            <figcaption>— {{ e.quote.source }}</figcaption>
          </figure>

          <div v-if="e.links?.length" class="tle__links">
            <a v-for="l in e.links" :key="l.url" :href="l.url" target="_blank" rel="noopener">{{ l.label }} →</a>
          </div>
        </div>
      </article>

      <!-- COMPACT entry: condensed but not anaemic; breaks the rhythm against rich blocks -->
      <article
        v-else
        :ref="(el: any) => { if (el) entryRefs[i] = el as HTMLElement; }"
        :data-coords="e.coords ? `${e.coords[0]},${e.coords[1]}` : ''"
        :data-year="formatYear(e)"
        :data-place="e.placeName || ''"
        :data-idx="i"
        :class="['tle', 'tle--compact', { 'tle--context': e.kind === 'context' }]"
      >
        <div class="tle--compact__inner">
          <span class="tle--compact__year">{{ formatYear(e) }}</span>
          <div class="tle--compact__body">
            <h4 class="tle--compact__title">{{ e.title }}</h4>
            <p class="tle--compact__txt" v-html="cleanBody(e.body)"></p>
            <div v-if="e.links?.length" class="tle--compact__links">
              <a v-for="l in e.links" :key="l.url" :href="l.url" target="_blank" rel="noopener">{{ l.label }} →</a>
            </div>
          </div>
        </div>
      </article>
    </template>

    <!-- Sticky scroll-synced mini-map; appears while inside this section -->
    <Teleport to="body">
      <TrackingMap
        :coords="activeCoords"
        :year="activeYear"
        :place-name="activePlace"
        :visible="mapVisible"
        :visited-coords="visitedCoords"
        :total-count="filteredEntries.length"
        :all-events="allEventsForMap"
        @jump-to-year="onJumpToYear"
      />
    </Teleport>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import VideoEmbed from './VideoEmbed.vue';
import TrackingMap from './TrackingMap.vue';
import KontinuitatStages from './embeds/KontinuitatStages.vue';
import KontinuitatRivers from './embeds/KontinuitatRivers.vue';
import KontinuitatMap from './KontinuitatMap.vue';
import MarcinelleBars from './embeds/MarcinelleBars.vue';
import AnarchistCarrara from './embeds/AnarchistCarrara.vue';
import PinholeRatio from './embeds/PinholeRatio.vue';
import StylithosMethod from './embeds/StylithosMethod.vue';
import ExhibitionsChart from './embeds/ExhibitionsChart.vue';

const embedMap: Record<string, unknown> = {
  'kontinuitat-stages': KontinuitatStages,
  'kontinuitat-rivers': KontinuitatRivers,
  'kontinuitat-route': KontinuitatMap,
  'marcinelle-bars': MarcinelleBars,
  'anarchist-carrara': AnarchistCarrara,
  'pinhole-ratio': PinholeRatio,
  'stylithos-method': StylithosMethod,
  'exhibitions-chart': ExhibitionsChart,
};

import { onMounted, onBeforeUnmount, nextTick, ref as vueRef } from 'vue';

const props = withDefaults(
  defineProps<{ from?: number; to?: number }>(),
  { from: 0, to: 9999 },
);

const activeCoords = vueRef<[number, number] | null>(null);
const activeYear = vueRef<string | null>(null);
const activePlace = vueRef<string | null>(null);
const activeIdx = vueRef<number>(-1);
const mapVisible = vueRef(false);
const entryRefs = vueRef<(HTMLElement | null)[]>([]);
let visibilityIo: IntersectionObserver | null = null;
const sectionRoot = vueRef<HTMLElement | null>(null);

// Visited = entries that have been scrolled past (their bottom went above viewport bottom)
const visitedSet = vueRef<Set<number>>(new Set());
const visitedCoords = computed<Array<[number, number]>>(() => {
  const out: Array<[number, number]> = [];
  // Include all entries up to and including activeIdx that have coords
  for (let i = 0; i <= activeIdx.value; i++) {
    const e = filteredEntries.value[i];
    if (e?.coords) out.push(e.coords);
  }
  return out;
});

const allEventsForMap = computed(() => {
  return filteredEntries.value
    .filter((e) => e.coords)
    .map((e) => ({
      year: e.year,
      endYear: e.endYear,
      yearText: formatYear(e),
      title: e.title,
      lane: e.lane,
      kind: e.kind,
      coords: e.coords!,
      placeName: e.placeName,
    }));
});

function onJumpToYear(year: number) {
  // Find the first entry matching this year and scroll to it
  for (let i = 0; i < filteredEntries.value.length; i++) {
    const e = filteredEntries.value[i];
    if (e.year === year) {
      const el = entryRefs.value[i];
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
  }
}

interface Entry {
  year: number; endYear?: number;
  hero?: boolean;
  kind?: 'context'; // pre-Stroobant context events (Carrara anarchism etc.)
  display?: 'rich' | 'compact'; // override the auto-derived layout density
  lane: 'life' | 'works' | 'exhibitions';
  title: string;
  body: string;
  img?: string; imgAlt?: string; imgCap?: string;
  imgFit?: 'cover' | 'contain' | 'top';
  videoId?: string;
  fact?: { num: string; unit?: string; label: string };
  links?: { url: string; label: string }[];
  embeds?: string[];
  quote?: { text: string; emphasis?: string; source: string };
  coords?: [number, number];
  placeName?: string;
}

const entries: Entry[] = [
  // ─── Pre-Stroobant context: Carrara anarchism ───
  {
    year: 1894, lane: 'life', kind: 'context',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: 'Carrara uprising against conscription',
    body: '<p>The marble quarrymen of <a href="https://en.wikipedia.org/wiki/Carrara" target="_blank" rel="noopener">Carrara</a> rise against state conscription. <strong>Eleven protesters are killed</strong> near the Dogali military base. The first major anarchist event in the city he will later call home. <a class="cite" href="https://www.carraramarbletour.it/en/anarchic-carrara/" target="_blank" rel="noopener">src</a></p>',
    img: '/img/04c20e-carrara-piazza-alberica.webp',
    imgAlt: 'Piazza Alberica, Carrara',
    imgCap: 'Piazza Alberica, Carrara — the old town the uprising swept through.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1900, lane: 'life', kind: 'context',
    coords: [45.59, 9.27], placeName: 'Monza, Italy',
    title: 'A Carrara anarchist kills the King of Italy',
    body: '<p><a href="https://en.wikipedia.org/wiki/Gaetano_Bresci" target="_blank" rel="noopener"><strong>Gaetano Bresci</strong></a>, active in the Carrara anarchist circles, assassinates <a href="https://en.wikipedia.org/wiki/Umberto_I" target="_blank" rel="noopener">King Umberto I</a> in <a href="https://en.wikipedia.org/wiki/Monza" target="_blank" rel="noopener">Monza</a> on 29 July. The Carrara anarchist tradition acquires its first international martyr. <a class="cite" href="https://en.wikipedia.org/wiki/Gaetano_Bresci" target="_blank" rel="noopener">src</a></p>',
    img: '/img/ce7569-bresci-gaetano.webp',
    imgAlt: 'Gaetano Bresci',
    imgCap: 'Gaetano Bresci, c. 1890s.<br /><span class="muted">Wikimedia Commons · public domain</span>',
    imgFit: 'top',
  },
  {
    year: 1911, lane: 'life', kind: 'context',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: '6½-hour workday won for the quarrymen',
    body: '<p>Anarchist-led unions win one of the earliest workplace victories in European history: a <strong>6½-hour workday</strong> for Carrara quarry workers. The labour structure Stroobant will later describe his own workshop as continuing. <a class="cite" href="https://www.carraramarbletour.it/en/anarchic-carrara/" target="_blank" rel="noopener">src</a></p>',
    img: '/img/b7abb0-lizzatura-con-cavatori.webp',
    imgAlt: 'Carrara quarrymen, historic lizzatura',
    imgCap: 'Carrara quarrymen with a marble block on the <em>lizza</em> — the medieval technique still in use through the 1960s.<br /><span class="muted">Wikimedia Commons · public domain</span>',
  },
  {
    year: 1944, lane: 'life', kind: 'context',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: 'Carrara women\'s rebellion · 7 July',
    body: '<p>Women of Carrara march against the <a href="https://en.wikipedia.org/wiki/Wehrmacht" target="_blank" rel="noopener">Wehrmacht</a>\'s evacuation order. The rebellion is preserved in the city\'s anarchist memory as a defining act of mass refusal. <a class="cite" href="https://www.carraramarbletour.it/en/anarchic-carrara/" target="_blank" rel="noopener">src</a></p>',
    img: '/img/a6c2ba-carrara-piazza-erbe-lapide-anniversario-protesta.webp',
    imgAlt: 'Carrara memorial plaque to the women\'s rebellion of 7 July 1944',
    imgCap: 'The memorial plaque on Piazza Erbe, Carrara — anniversary of the women\'s rebellion against evacuation, 7 July 1944.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1945, lane: 'life', kind: 'context',
    coords: [44.08, 10.10], placeName: 'Carrara · Teatro Politeama',
    title: 'FAI founded in Carrara · 19 September',
    body: '<p>The <a href="https://en.wikipedia.org/wiki/Italian_Anarchist_Federation" target="_blank" rel="noopener"><strong>Federazione Anarchica Italiana</strong></a> is founded at the First Anarchist Congress, held at the Teatro Politeama in Carrara. Twenty-five years later, Stroobant will arrive in town. Fifty-six years later, the FAI will commission his Barcelona bas-relief. <a class="cite" href="https://en.wikipedia.org/wiki/Italian_Anarchist_Federation" target="_blank" rel="noopener">src</a></p>',
    img: '/img/1e01be-palazzo-politeama-in-carrara.webp',
    imgAlt: 'Palazzo Politeama, Carrara',
    imgCap: 'Teatro / Palazzo Politeama, Piazza Matteotti, Carrara — the building where the FAI was founded.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    links: [{ url: 'https://en.wikipedia.org/wiki/Italian_Anarchist_Federation', label: 'FAI — Wikipedia' }],
  },

  // ─── Stroobant\'s life ───
  {
    year: 1947, lane: 'life', hero: true,
    coords: [51.22, 4.40], placeName: 'Antwerp, Belgium',
    title: 'Born in Antwerp',
    body: '<p>Sixteenth of March, <a href="https://en.wikipedia.org/wiki/Antwerp" target="_blank" rel="noopener">Antwerp</a>. Belgian, Flemish. The painter and lithographer <a href="https://en.wikipedia.org/wiki/Fran%C3%A7ois_Stroobant" target="_blank" rel="noopener"><strong>François Stroobant</strong></a> (1819–1916) is from the same Flemish family lineage, although no documented direct kinship between him and Dominique has surfaced. <a class="cite" href="https://en.wikipedia.org/wiki/Dominique_Stroobant" target="_blank" rel="noopener">src</a></p>',
    img: '/img/30137e-011-grote-markt-in-antwerp-belgium-creative-comm.webp',
    imgAlt: 'Grote Markt, Antwerp',
    imgCap: 'Antwerp Grote Markt — the heart of the Flemish port-city he was born into.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    quote: { text: 'I already knew at 13 I would be a stonemason. Stone provided necessary resistance.', emphasis: 'necessary resistance', source: 'Stroobant, solo-show essay, 2015' },
    links: [{ url: 'https://en.wikipedia.org/wiki/Dominique_Stroobant', label: 'Wikipedia' }],
  },
  {
    year: 1966, endYear: 1970, lane: 'life', hero: true,
    coords: [50.85, 4.35], placeName: 'Brussels, Belgium',
    title: 'École Saint-Luc, Brussels',
    body: '<p>Pupil of <strong>Stan Hensen</strong> and <strong>Jean Guiraud</strong>. During these student years he already carves outdoors — mostly with stones salvaged from demolition sites in <a href="https://en.wikipedia.org/wiki/Soignies" target="_blank" rel="noopener"><strong>Soignies</strong></a>, the Belgian "petit granit" / blue-limestone region. Antwerp is at this moment one of Europe\'s hottest contemporary-art cities: <a href="https://en.wikipedia.org/wiki/Wide_White_Space_Gallery" target="_blank" rel="noopener">Wide White Space Gallery</a> shows <a href="https://en.wikipedia.org/wiki/Joseph_Beuys" target="_blank" rel="noopener">Beuys</a>, <a href="https://en.wikipedia.org/wiki/Marcel_Broodthaers" target="_blank" rel="noopener">Broodthaers</a>, <a href="https://en.wikipedia.org/wiki/Carl_Andre" target="_blank" rel="noopener">Carl Andre</a>, <a href="https://en.wikipedia.org/wiki/Richard_Long_(artist)" target="_blank" rel="noopener">Long</a>, <a href="https://en.wikipedia.org/wiki/Piero_Manzoni" target="_blank" rel="noopener">Manzoni</a> and <a href="https://en.wikipedia.org/wiki/Panamarenko" target="_blank" rel="noopener">Panamarenko</a>. Stroobant is twenty-two when he graduates — and he leaves for Carrara almost immediately. <a class="cite" href="https://axel-vervoordt.s3.amazonaws.com/documents/DOMINIQUE_STROOBANT_BIO-website.pdf" target="_blank" rel="noopener">Vervoordt PDF</a></p>',
    img: '/img/af8266-campus-sint-lukas-luca-school-of-arts.webp',
    imgAlt: 'Campus Sint-Lukas, the Saint-Luc / LUCA School of Arts in Brussels',
    imgCap: 'Campus Sint-Lukas, Brussels — the Saint-Luc institute (now part of LUCA School of Arts) where he studied.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    links: [{ url: 'https://axel-vervoordt.s3.amazonaws.com/documents/DOMINIQUE_STROOBANT_BIO-website.pdf', label: 'Vervoordt biographical PDF' }],
  },
  {
    year: 1968, lane: 'life', kind: 'context',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: 'IAF-IFA founded in Carrara · ~90% anarchist in region',
    body: '<p>An international congress in Carrara founds the <a href="https://en.wikipedia.org/wiki/International_of_Anarchist_Federations" target="_blank" rel="noopener"><strong>International of Anarchist Federations</strong></a>. At its peak the same year, an estimated <strong>~90% of Carrara and the surrounding region</strong> identify as anarchist. This is the population Stroobant will move into two years later. <a class="cite" href="https://en.wikipedia.org/wiki/International_of_Anarchist_Federations" target="_blank" rel="noopener">src</a></p>',
    img: '/img/a77c4d-bandiera-f-a-i.png',
    imgAlt: 'The black-red flag of the Federazione Anarchica Italiana',
    imgCap: 'The FAI black-red flag — heir to the 1945 Politeama congress and host of the 1968 IAF international.<br /><span class="muted">Wikimedia Commons · public domain</span>',
    imgFit: 'contain',
  },
  {
    year: 1968, lane: 'works', hero: true,
    coords: [51.1962, 4.4194], placeName: 'Brialmontpark, Berchem',
    title: 'The 15-ton elephant · Brialmontpark, Berchem',
    body: '<p>Age 21. Antwerp tears down its <a href="https://en.wikipedia.org/wiki/Brialmont_Fortifications" target="_blank" rel="noopener">Brialmont fortifications</a> to build the Ring road; one of the city gates — the <strong>Berchemsepoort</strong> — gives up its monumental keystone. Stroobant carves it into an abstract block and sets it in <strong>Brialmontpark</strong> in <a href="https://en.wikipedia.org/wiki/Berchem" target="_blank" rel="noopener">Berchem</a>. The children walk past, look once, and name it on their own: <em>"de kinderen noemen het een olifant"</em> — "the children call it an elephant". He accepts the rename as the title.</p><p>It is still in the park, maintained by the city\'s PlusTeam public-art crew. His earliest documented public sculpture — predating both Floating Stones and his move to Carrara. <a class="cite" href="https://antwerpspersbureau.be/lapis-arte-en-stroom-maatwerk-onderhouden-260-kunstwerken-in-heel-de-stad/" target="_blank" rel="noopener">Antwerps Persbureau</a></p>',
    fact: { num: '15', unit: 't', label: 'Weight of the salvaged gate keystone' },
    links: [
      { url: 'https://www.openstreetmap.org/?mlat=51.1962&mlon=4.4194#map=17/51.1962/4.4194', label: 'Brialmontpark on the map' },
      { url: 'https://antwerpspersbureau.be/lapis-arte-en-stroom-maatwerk-onderhouden-260-kunstwerken-in-heel-de-stad/', label: 'Antwerpse Persbureau on the sculpture' },
    ],
  },
  {
    year: 1969, lane: 'life',
    coords: [-33.92, 18.42], placeName: 'Cape Town · S. Africa',
    title: 'Meets Mona Johnson',
    body: '<p>He meets <strong>Mymona "Mona" Johnson</strong>, a South African woman from <a href="https://en.wikipedia.org/wiki/Cape_Town" target="_blank" rel="noopener">Cape Town</a>. They marry in 1973. Their son <strong>Ish-maël</strong> is born in 1972; their daughter <strong>Mascha</strong> in 1978 (who later becomes a marine botanist / <a href="https://www.wikidata.org/wiki/Q41224056" target="_blank" rel="noopener">phycologist</a>). <a class="cite" href="https://axel-vervoordt.s3.amazonaws.com/documents/DOMINIQUE_STROOBANT_BIO-website.pdf" target="_blank" rel="noopener">Vervoordt PDF</a></p>',
    img: '/img/6f5fe2-cape-town-za-table-mountain-2024-2794-96-98-2800.webp',
    imgAlt: 'Table Mountain over Cape Town',
    imgCap: 'Cape Town under Table Mountain — Mona\'s home city, 9,000 km from his.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1970, lane: 'life', hero: true,
    coords: [44.0835, 10.1156], placeName: 'Miseglia, Carrara',
    title: 'State scholarship · settles in Miseglia, Carrara',
    body: '<p>A Belgian state scholarship sends him to the <a href="https://en.wikipedia.org/wiki/Apuan_Alps" target="_blank" rel="noopener">Apuan Alps</a>. He settles in <a href="https://en.wikipedia.org/wiki/Miseglia" target="_blank" rel="noopener"><strong>Miseglia</strong></a>, a hamlet of fewer than 500 people at 240 m elevation, perched between two Roman-era quarry canyons — Canale di Fantiscritti and Canalgrande. The <a href="https://en.wikipedia.org/wiki/Italian_Anarchist_Federation" target="_blank" rel="noopener">Italian Anarchist Federation</a> has been headquartered down the road since 1945; an estimated 90% of the surrounding region identifies as anarchist at this moment.</p><p>He never lived anywhere else after. <a class="cite" href="https://en.wikipedia.org/wiki/Miseglia" target="_blank" rel="noopener">Wikipedia</a></p>',
    img: '/img/64388d-2017-05-28-via-fantiscritti-con-chiesa-spirito-s.webp',
    imgAlt: 'Miseglia village',
    imgCap: 'Miseglia. Santo Spirito church, rebuilt 1617.<br /><span class="muted">Wikimedia Commons · CC BY-SA 4.0</span>',
    fact: { num: '56', unit: 'yrs', label: 'And counting, in one hamlet' },
  },
  {
    year: 1971, lane: 'works',
    coords: [51.18, 4.40], placeName: 'Middelheim · Antwerp',
    title: 'First sculpture acquired · Middelheim Museum, Antwerp',
    body: '<p>The <a href="https://middelheimmuseum.be/en" target="_blank" rel="noopener">Middelheim Open-Air Sculpture Museum</a> in Antwerp acquires a sculpture by him. The work has its own <a href="https://www.wikidata.org/wiki/Q27616264" target="_blank" rel="noopener">Wikidata entry</a> (Q27616264) and is catalogued under inventory <strong>MID.B.417</strong>. It is his earliest publicly held sculpture, and chronologically it precedes the Floating Stones Group by a year. <a class="cite" href="https://www.wikidata.org/wiki/Q27616264" target="_blank" rel="noopener">Wikidata</a></p>',
    img: '/img/65b395-sculptures-in-beeldentuin-middelheim-museum-02.webp',
    imgAlt: 'Sculptures in the Middelheim open-air museum, Antwerp',
    imgCap: 'The Middelheim sculpture park, Antwerp — first institution to hold a Stroobant.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    fact: { num: 'MID.B.417', label: 'Inventory № at Middelheim' },
    links: [
      { url: 'https://www.wikidata.org/wiki/Q27616264', label: 'Wikidata Q27616264' },
      { url: 'https://middelheimmuseum.be/en', label: 'Middelheim Museum' },
    ],
  },
  {
    year: 1972, lane: 'works', hero: true, display: 'rich',
    coords: [51.22, 4.40], placeName: 'Galleria Penta · Antwerp',
    title: 'Founds Floating Stones Group · first lithophone · first solo',
    body: '<p>Three things happen in this year. He co-founds the <strong>Floating Stones Group</strong> with the American sculptor Kenneth Davis and the Belgian sculptor Philippe Toussaint. His first solo exhibition opens at <strong>Galleria Penta</strong> in Antwerp. And in the workshop he starts his first <em>"lithophonic experiments"</em> — musical instruments built out of stone. The same instinct that produces his pinhole cameras five years later: turning sculptural material into a recording device.</p><p>His son <strong>Ish-maël</strong> is born this year. <a class="cite" href="https://axel-vervoordt.s3.amazonaws.com/documents/DOMINIQUE_STROOBANT_BIO-website.pdf" target="_blank" rel="noopener">Vervoordt PDF</a></p>',
    img: '/img/c6569f-stroobant-dominique.webp',
    imgAlt: 'Stroobant in his workshop',
    imgCap: 'Stroobant in the Carrara workshop — the first crew that becomes Floating Stones.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    fact: { num: '3', label: 'Co-founders · Davis · Toussaint · Stroobant' },
  },
  {
    year: 1972, lane: 'works',
    coords: [47.92, 16.16], placeName: 'Lindabrunn, Austria',
    title: 'Sculpture symposium · Lindabrunn, Austria',
    body: '<p>First of four international sculpture symposia he attends across the 1970s. <a href="https://de.wikipedia.org/wiki/Symposion_europ%C3%A4ischer_Bildhauer_Lindabrunn" target="_blank" rel="noopener">Lindabrunn</a> sits south of Vienna and hosts an annual stone-sculpture symposium founded by Mathias Hietz. Among the regulars there: the Austrian sculptor <a href="https://en.wikipedia.org/wiki/Karl_Prantl" target="_blank" rel="noopener"><strong>Karl Prantl</strong></a>, whose work Stroobant will later credit as part of the same anti-industrial stone tradition.</p>',
    img: '/img/9a4875-karl-prantl-am-stein.webp',
    imgAlt: 'Karl Prantl with a stone',
    imgCap: 'Karl Prantl (1923–2010) — Austrian sculptor and a recurring presence in the same symposia.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    fact: { num: '1st', label: 'Of 4 sculpture symposia · 1972 · 74 · 78 · 80' },
  },
  {
    year: 1973, lane: 'life',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: 'Marries Mona Johnson · meets Jef Verheyen',
    body: '<p>Marries Mona Johnson in Carrara. The same year he meets <a href="https://en.wikipedia.org/wiki/Jef_Verheyen" target="_blank" rel="noopener"><strong>Jef Verheyen</strong></a>, the Belgian <a href="https://en.wikipedia.org/wiki/Zero_(art)" target="_blank" rel="noopener">ZERO-movement</a> painter. They will collaborate on marble and granite works through the 1970s, including Verheyen\'s visits to Carrara in 1977.</p>',
  },
  {
    year: 1973, lane: 'exhibitions',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: 'VII Biennale di Carrara · plus three more group shows',
    body: '<p>A heavy year for group shows: <em>VII Biennale di Carrara</em>; <em>XI Mostra Scultura all\'Aperto</em> at the Fondazione Pagani in Legnano; <em>Cinq sculpteurs, cinq matières</em> at the Musée d\'Ixelles in Brussels; and <em>Derde dimensie</em> at the Richard Foncke gallery in Ghent.</p>',
    img: '/img/2026b1-carrara-piazza-alberica-fontana-della-beatrice-1.webp',
    imgAlt: 'Piazza Alberica, Carrara — Fontana della Beatrice',
    imgCap: 'Piazza Alberica, Carrara — host city of his first Carrara Biennale.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1974, lane: 'exhibitions',
    coords: [51.18, 4.40], placeName: 'Middelheim · Antwerp',
    title: 'Belgische beeldhouwkunst · Middelheim, Antwerp',
    body: '<p>Group show <em>Belgische beeldhouwkunst</em> at the Middelheim Museum — likely the channel through which the 1971 sculpture (inv. MID.B.417) entered the permanent collection.</p>',
    img: '/img/c93811-la-rivi-re-by-aristide-maillol-beeldentuin-midde.webp',
    imgAlt: 'Aristide Maillol, La Rivière — Middelheim sculpture park, Antwerp',
    imgCap: 'A Maillol bronze in the Middelheim park — the kind of Belgian sculpture-canon company his 1971 acquisition entered.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1974, lane: 'works',
    coords: [48.14, 16.42], placeName: 'Vienna, Austria',
    title: 'Sculpture symposium · W.I.G. 74, Vienna',
    body: '<p>Wiener Internationale Gartenschau 74 — a horticultural expo at Kurpark Oberlaa that ran from 18 April to 14 October 1974 and hosted a sculpture programme. Stroobant participates.</p>',
    img: '/img/275899-kurpark-oberlaa-staudengarten.webp',
    imgAlt: 'Kurpark Oberlaa, Vienna',
    imgCap: 'Kurpark Oberlaa, Vienna — the WIG 74 site, still a public park.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1975, lane: 'works', hero: true,
    coords: [44.0871, 10.1262], placeName: 'Miseglia · Via Fantiscritti 11',
    title: 'Laboratorio STYLITHOS opens · Via Fantiscritti 11',
    body: '<p>He opens his own workshop at the mouth of the Fantiscritti quarry canyon, originally registered under his own name and renamed <strong>Stylithos</strong> in 2010 (Greek <em>lithos</em> — stone). The shop is organised, by his own description, like a medieval stonemasons\' lodge — small crew, oral transmission of technique, no industrial production line.</p><p>Same year: he produces a <em>"World\'s biggest granite stamp"</em> for the Antwerp Pop / Happenings artist <strong>Wout Vercammen</strong> — a granite block parody of an actual postal stamp.</p>',
    img: '/img/3978a6-libreria-casa-stroobant-miseglia.webp',
    imgAlt: 'Stroobant\'s house in Miseglia',
    imgCap: 'Bookshelves inside the Stroobant house in Miseglia.<br /><span class="muted">Wikimedia Commons · CC BY-SA 4.0</span>',
    links: [{ url: 'http://www.museodeibozzetti.it/assets/files/mdb/collezione/laboratori/s001283.php', label: 'Museo dei Bozzetti studio record' }],
  },
  {
    year: 1976, lane: 'works',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: '"Behind those stones" · documentary with Dirk Van den Eynde',
    body: '<p>A film and videotape "essay about the antiauthoritarian use of stone", made with <strong>Dirk Van den Eynde</strong>. Contributing voices include the Austrian sculptor <strong>Karl Prantl</strong> (the giant of post-war Austrian stone sculpture), Matthias Hietz, and the quarry workers of the <strong>Lega Cavatori di Torano</strong> — the Torano stonecutters\' union. Produced by the Flemish Ministry of the Arts. It screens at the Venice Biennale that same year.</p>',
  },
  {
    year: 1976, lane: 'works',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: 'Begins collaboration with Fratelli Biselli, Carrara',
    body: '<p>Starts working with the Carrara granite-cutting firm <strong>Fratelli Biselli</strong> — one of the first two enterprises to cut granite in Carrara, operational since the 1950s.</p>',
    img: '/img/ab0890-carrara-marble-quarry-face.webp',
    imgAlt: 'A Carrara quarry face',
    imgCap: 'A Carrara quarry face — the kind of material his crew works.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1976, lane: 'exhibitions',
    coords: [45.44, 12.33], placeName: 'Venice Biennale',
    title: '37th Venice Biennale · Behind these Stones',
    body: '<p>Presents <em>Behind these Stones / Dietro i sassi</em> in the Film and Photography Catalogue of the 37th Venice Biennale, on the Flemish Ministry of the Arts pavilion.</p>',
    img: '/img/ec07d8-giardini-biennale-brazilian-pavilion-2011.webp',
    imgAlt: 'Giardini of the Venice Biennale — Brazilian pavilion',
    imgCap: 'Giardini della Biennale, Venice — the permanent home of the international pavilions where his film screened in 1976.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1976, lane: 'exhibitions',
    coords: [51.22, 4.40], placeName: 'Spectrum · Antwerp',
    title: 'Steentuigen · Floating Stones · Spectrum, Antwerp',
    body: '<p>The Floating Stones trio (Stroobant + Davis + Toussaint) holds a joint exhibition at Spectrum in Antwerp.</p>',
    img: '/img/efa002-de-meir-antwerpen.webp',
    imgAlt: 'De Meir, the central Antwerp boulevard',
    imgCap: 'De Meir — the central Antwerp boulevard the city\'s gallery scene radiates from.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1977, lane: 'works', hero: true,
    coords: [44.0835, 10.1156], placeName: 'Miseglia, Carrara',
    title: 'First heliograph · he builds his own pinhole camera',
    body: '<p>He had been carving stone sundials. He started wondering what the sundial itself sees over a day, a season, a year. The answer is a luminous arc traced on photographic paper. He builds his first <a href="https://en.wikipedia.org/wiki/Pinhole_camera" target="_blank" rel="noopener"><strong>pinhole camera</strong></a> — a "sunshine-recorder" — and exposes it for a day. By 1979 he is up to <strong>six-month exposures</strong>. He becomes, alongside the Italian filmmaker <a href="https://en.wikipedia.org/wiki/Paolo_Gioli" target="_blank" rel="noopener"><strong>Paolo Gioli</strong></a>, one of the two European pioneers of pinhole-as-art-form.</p><p>Same year: he wins the <strong>Eugène Baie prize</strong> from the Province of Antwerp for sculpture. Every camera is self-built — by his rule, <em>"my tools could not be ready-made"</em>. He deploys multiple cameras at different angles, horizon to zenith; inside each, slow litho film is stacked in layers, and after development he picks the sheet that exposed both landscape and solar arc correctly.</p><p>For the 2002 <em>Senza Obiettivo</em> exhibition in <a href="https://en.wikipedia.org/wiki/Siena" target="_blank" rel="noopener">Siena</a> he scales the same idea up to architectural size: a <strong>3 × 3 m camera obscura with variable aperture</strong>. For <a href="https://en.wikipedia.org/wiki/Geneva" target="_blank" rel="noopener">Geneva</a>, 1982: a <strong>10-metre-long</strong> pinhole emulsion. <a class="cite" href="https://obscura-photography.co.uk/history" target="_blank" rel="noopener">Obscura</a> <a class="cite" href="https://pinholeresource.com/pinhole-journal-55-back-issues/" target="_blank" rel="noopener">Pinhole Journal</a></p>',
    img: '/img/096219-solari117p.webp',
    imgAlt: 'A solargraph — sun paths over a year',
    imgCap: 'Sun paths recorded on photographic paper — the technique he pioneered.<br /><span class="muted">Wikimedia Commons</span>',
    embeds: ['pinhole-ratio'],
    fact: { num: '6', unit: 'mo', label: 'Longest pinhole exposure' },
    quote: { text: 'The slow process of etching the image into the material is the connection between pinhole photography and sculpting — the light and the tool both leave their marks.', emphasis: 'leave their marks', source: 'Stroobant, Vervoordt catalogue 2012' },
    links: [
      { url: 'https://obscura-photography.co.uk/history', label: 'Obscura Photography on Stroobant' },
      { url: 'https://pinholeresource.com/pinhole-journal-55-back-issues/', label: 'Pinhole Journal Vol. 17 #1 — dedicated issue' },
    ],
  },
  {
    year: 1978, lane: 'works',
    coords: [36.05, 138.10], placeName: 'Lake Suwa, Japan',
    title: 'Sculpture symposium · Suwako, Japan',
    body: '<p>The only documented Japan trip in his career. He participates in a stone-sculpture symposium at <a href="https://en.wikipedia.org/wiki/Lake_Suwa" target="_blank" rel="noopener"><strong>Lake Suwa</strong></a> (Suwako) in <a href="https://en.wikipedia.org/wiki/Nagano_Prefecture" target="_blank" rel="noopener">Nagano prefecture</a>, in central Honshū — a glacial lake that <a href="https://en.wikipedia.org/wiki/Hokusai" target="_blank" rel="noopener">Hokusai</a> also painted from this angle two centuries earlier.</p>',
    img: '/img/405c56-lake-suwa-as-seen-from-tateishi-park-east.webp',
    imgAlt: 'Lake Suwa, Nagano',
    imgCap: 'Lake Suwa from Tateishi Park, Nagano.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1978, lane: 'life',
    coords: [44.0835, 10.1156], placeName: 'Miseglia, Carrara',
    title: 'Daughter Mascha born',
    body: '<p>His daughter <strong>Mascha Stroobant</strong> is born. She goes on to become a marine botanist / phycologist, with her own Wikidata entry (Q41224056). The father carves marble; the daughter studies algae.</p>',
  },
  {
    year: 1978, lane: 'exhibitions',
    coords: [51.22, 4.40], placeName: 'Spectrum · Antwerp',
    title: 'Gnomon · Spectrum, Antwerp · Venice Biennale (returning)',
    body: '<p>Solo show <em>Gnomon</em> at Spectrum in Antwerp. The name — Greek for the rod of a sundial — comes from the same instinct that produced the heliograph one year earlier. He also re-presents <em>Behind those Stones</em> at the Venice Biennale this year.</p>',
    img: '/img/d09337-sundial-with-analemma-botanical-garden-d.webp',
    imgAlt: 'A sundial with analemma',
    imgCap: 'A sundial — the <em>gnomon</em> is the rod that casts the shadow. The title is direct.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1979, lane: 'exhibitions',
    coords: [43.73, 10.81], placeName: 'Fucecchio · Tuscany',
    title: 'Eliofanografie · Biblioteca Comunale di Fucecchio, Tuscany',
    body: '<p>His first dedicated pinhole-photography solo show — at the public library of Fucecchio, a small Tuscan town. The title <em>Eliofanografie</em> roughly means "sun-image recordings". The show comes two years after the first heliograph and is the first public framing of the technique as art.</p>',
    img: '/img/6eb0b2-rocca-fiorentina-fucecchio-04.webp',
    imgAlt: 'Rocca fiorentina, Fucecchio',
    imgCap: 'Fucecchio, Tuscany — the Florentine fortress above the town that hosted his first solo pinhole show.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1979, lane: 'works', endYear: 1981,
    coords: [44.0835, 10.1156], placeName: 'Miseglia, Carrara',
    title: 'Steenklok V',
    body: '<p>One of the few explicitly catalogued sculptures from his early Carrara years.</p>',
  },
  {
    year: 1979, lane: 'exhibitions',
    coords: [50.85, 4.35], placeName: 'Brussels, Belgium',
    title: 'Suivre le jour · Anne Van Hoorenbeeck, Brussels',
    body: '<p>A second pinhole solo, in Brussels — <em>"To follow the day. For an instrumental sculpture"</em>.</p>',
    img: '/img/33b735-glise-notre-dame-du-sablon-brussels-4645895991.webp',
    imgAlt: 'Notre Dame du Sablon, Brussels',
    imgCap: 'Notre Dame du Sablon, Brussels — the Sablon, the city\'s gallery district.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1980, lane: 'works',
    coords: [51.84, 4.97], placeName: 'Gorinchem, Netherlands',
    title: 'Dag in dag uit · Kunstcentrum Badhuis, Gorinchem',
    body: '<p>A small stapled monograph — 24 pages, 14.9 × 10.4 cm, two-colour offset — published by Kunstcentrum Badhuis in Gorinchem, Netherlands, as part of their <em>Kunstinformatie</em> series. The first publication centred on his work.</p>',
    img: '/img/ee3ceb-gorinchem-004.webp',
    imgAlt: 'Gorinchem, Netherlands',
    imgCap: 'Gorinchem, Netherlands — the small Dutch river-town that published his first monograph.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    fact: { num: '24', unit: 'pp', label: 'First monograph · 14.9 × 10.4 cm · stapled' },
    links: [{ url: 'https://cdla.info/2020/02/28/kunstinformatie/', label: 'Centre du livre d\'artiste — Kunstinformatie' }],
  },
  {
    year: 1980, lane: 'exhibitions',
    coords: [51.22, 4.40], placeName: 'ICC · Antwerp',
    title: 'Hole-o-graphy · ICC, Antwerp (with Terrence Dinnan)',
    body: '<p>Solo two-artist show with Terrence Dinnan at the International Cultural Centre in Antwerp. The ICC was housed in the <strong>Paleis op de Meir</strong>, a former royal residence on Antwerp\'s central boulevard — Belgium\'s flagship contemporary-art space from 1970 to 1990.</p>',
    img: '/img/f63249-paleis-op-de-meir-antwerp.webp',
    imgAlt: 'Paleis op de Meir, Antwerp — the home of the ICC',
    imgCap: 'Paleis op de Meir, Antwerp — the former royal palace that housed the ICC contemporary-art centre, 1970–1990.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1980, lane: 'works',
    coords: [48.34, 7.87], placeName: 'Lahr-Schwarzwald, Germany',
    title: 'Sculpture symposium · Lahr-Schwarzwald, Germany',
    body: '<p>The fourth and last international sculpture symposium of his early career — at Lahr in the Black Forest.</p>',
    img: '/img/db2cc0-blick-ber-lahr-hirschplatz.webp',
    imgAlt: 'Lahr in the Black Forest, Germany',
    imgCap: 'Lahr/Schwarzwald — the Black Forest town that hosted his last symposium.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    fact: { num: '4th', label: 'And last sculpture symposium · Black Forest' },
  },
  {
    year: 1982, lane: 'works',
    coords: [46.20, 6.14], placeName: 'Geneva, Switzerland',
    title: 'Camera Oscura · 10-metre pinhole emulsion, Geneva',
    body: '<p>He publishes <em>Camera Oscura</em> with co-author <strong>Hendel Teicher</strong> and, the same year, builds a <strong>10-metre-long</strong> pinhole emulsion in Geneva — an extreme experiment in scale. Solo shows that year at Voltaire / L\'Association des Amis du Mamco and at the Canon Photo Gallery, both in Geneva.</p>',
    img: '/img/c50187-fotografia0346.webp',
    imgAlt: 'A solargraph camera made of a tin can',
    imgCap: 'A canonical solargraph camera — tin can, pinhole, photographic paper. Stroobant\'s cameras were the same idea at much larger scales.<br /><span class="muted">Wikimedia Commons</span>',
    fact: { num: '10', unit: 'm', label: 'Length of the Geneva emulsion' },
  },
  {
    year: 1983, endYear: 1986, lane: 'works', hero: true,
    coords: [50.11, 8.67], placeName: 'Frankfurt am Main',
    title: 'Kontinuität · Max Bill\'s 80-ton Möbius',
    body: '<p>In <a href="https://en.wikipedia.org/wiki/Sardinia" target="_blank" rel="noopener">Sardinia</a>, a <strong>500-ton block</strong> of Rosa Sardo granite is freed at the <strong>Scarraciana quarry</strong> near <a href="https://en.wikipedia.org/wiki/Tempio_Pausania" target="_blank" rel="noopener">Tempio Pausania</a> on 20 October 1983. Pieces are blasted off on site; <strong>180 tons</strong> sail to Marina di Carrara, arriving 20 February 1984. There, over <strong>two and a half years</strong>, Stroobant\'s crew remove another hundred tons by hand and diamond wire, scaling <a href="https://en.wikipedia.org/wiki/Max_Bill" target="_blank" rel="noopener">Bill</a>\'s small plaster maquette 4× onto the granite with a three-compass jig — the medieval <em>Drei-Zirkelmethode</em>.</p><p>The finished 80-ton sculpture sails through <strong>Gibraltar</strong> to Rotterdam, up the Rhine and Main to <a href="https://en.wikipedia.org/wiki/Frankfurt" target="_blank" rel="noopener">Frankfurt</a>, and on <strong>7 September 1986</strong> is lifted onto its plinth at <a href="https://en.wikipedia.org/wiki/Taunusanlage" target="_blank" rel="noopener">Taunusanlage</a> in front of the <a href="https://en.wikipedia.org/wiki/Deutsche_Bank" target="_blank" rel="noopener">Deutsche Bank</a> towers. No plaque ever names him. In the canonical German maths-press write-up — Gerd Fischer\'s 1999 essay in <em>DMV-Mitteilungen</em> — his name does not appear at all. <a class="cite" href="https://de.wikipedia.org/wiki/Kontinuit%C3%A4t_(Skulptur)" target="_blank" rel="noopener">Wikipedia DE</a> <a class="cite" href="https://www.degruyterbrill.com/document/doi/10.1515/dmvm-1999-0410/pdf" target="_blank" rel="noopener">Fischer 1999</a></p>',
    img: '/img/424d22-kontinuitaet-1986-max-bill-2012-ffm-849.webp',
    imgAlt: 'Kontinuität, Frankfurt',
    imgCap: 'The 80-ton finished sculpture in front of Deutsche Bank.<br /><span class="muted">Photo: Dontworry · Wikimedia Commons · CC BY-SA 4.0</span>',
    fact: { num: '500 t → 80 t', label: 'Quarry block to installed sculpture' },
    embeds: ['kontinuitat-stages', 'kontinuitat-rivers', 'kontinuitat-route'],
    links: [
      { url: 'https://de.wikipedia.org/wiki/Kontinuit%C3%A4t_(Skulptur)', label: 'Kontinuität — Wikipedia (DE)' },
      { url: 'https://www.degruyterbrill.com/document/doi/10.1515/dmvm-1999-0410/pdf', label: 'Fischer 1999 essay (PDF)' },
    ],
  },
  {
    year: 1984, lane: 'exhibitions',
    coords: [47.20, 7.39], placeName: 'Grenchen, Switzerland',
    title: 'Steine, eine Auswahl · Galerie Bernard, Grenchen, Switzerland',
    body: '<p>Solo show of stone work at a Swiss gallery.</p>',
    img: '/img/692031-grenchen-waldrand.webp',
    imgAlt: 'Grenchen, Switzerland',
    imgCap: 'Grenchen, Solothurn canton — a small Swiss city at the foot of the Jura.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1985, lane: 'exhibitions', endYear: 1986,
    coords: [45.77, 4.88], placeName: 'Villeurbanne, France',
    title: "L'art et le temps · Institut d'art contemporain de Villeurbanne",
    body: '<p>Group show <em>"L\'art et le temps. Regards sur la quatrième dimension"</em> (Art and Time: Views on the Fourth Dimension), Dec 1985 – Jan 1986, Villeurbanne, France. A thematic exhibition organising the heliograph work into a wider European discourse on time-based art.</p>',
    img: '/img/2df7e0-villeurbanne-cours-mile-zola-terminus-du-tramway.webp',
    imgAlt: 'Cours Émile-Zola, Villeurbanne',
    imgCap: 'Villeurbanne, France — the Lyon suburb that hosts the Institut d\'art contemporain.<br /><span class="muted">Wikimedia Commons · public domain</span>',
  },
  {
    year: 1985, lane: 'exhibitions',
    coords: [46.57, 8.41], placeName: 'Furka Pass · Uri, Switzerland',
    title: 'Camera obscura of snow · Furk\'Art, Uri, Switzerland',
    img: '/img/ac4ec4-furkapass-westside.webp',
    imgAlt: 'Furka Pass, Swiss Alps',
    imgCap: 'Furka Pass, Swiss Alps — the high-altitude site of the Furk\'Art camera obscura.<br /><span class="muted">Wikimedia Commons · CC BY-SA 4.0</span>',
    body: '<p>A pinhole work made specifically for the high-altitude Furk\'Art project in the Swiss Alps.</p>',
  },
  {
    year: 1988, lane: 'works', hero: true,
    coords: [50.4054, 4.4486], placeName: 'Marcinelle, Charleroi',
    title: 'Marcinelle · stone for 262 dead miners',
    body: '<p>On 8 August 1956 an electric fire in the loading cage ignited the <a href="https://en.wikipedia.org/wiki/Bois_du_Cazier" target="_blank" rel="noopener">Bois du Cazier</a> coal mine near <a href="https://en.wikipedia.org/wiki/Charleroi" target="_blank" rel="noopener">Charleroi</a>. <strong>262 of 274 men underground died.</strong> Of them, <strong>136 were Italian guest workers</strong> shipped north under the post-war "men for coal" treaty between Rome and Brussels.</p><p>Stroobant — a Belgian who in 1970 had emigrated <em>south</em> to the marble valleys those miners had come from — carves their memorial inside the mine site in 1988. The site becomes <a href="https://whc.unesco.org/en/list/1344/" target="_blank" rel="noopener">UNESCO World Heritage</a> in 2012. <em>"Così ho scolpito i morti di Marcinelle"</em>, he tells <em>Il Tirreno</em> in 2012 — "this is how I sculpted the dead of Marcinelle". <a class="cite" href="https://en.wikipedia.org/wiki/Marcinelle_mining_disaster" target="_blank" rel="noopener">Wikipedia</a> <a class="cite" href="https://iltirreno.gelocal.it/massa/cronaca/2012/08/13/news/cosi-ho-scolpito-i-morti-di-marcinelle-1.5543047" target="_blank" rel="noopener">Il Tirreno 2012</a></p>',
    img: '/img/faba3b-monument-d-di-aux-victimes-de-bois-du-cazier.webp',
    imgAlt: 'Stroobant\'s 1988 memorial at Bois du Cazier',
    imgCap: '<strong>His own memorial sculpture at Bois du Cazier, 1988</strong> — a rare public photograph of one of Stroobant\'s solo monuments.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    fact: { num: '136', unit: '/ 262', label: 'Of the dead were Italian migrants' },
    embeds: ['marcinelle-bars'],
    links: [
      { url: 'https://en.wikipedia.org/wiki/Marcinelle_mining_disaster', label: 'Marcinelle disaster — Wikipedia' },
      { url: 'https://iltirreno.gelocal.it/massa/cronaca/2012/08/13/news/cosi-ho-scolpito-i-morti-di-marcinelle-1.5543047', label: 'Il Tirreno 2012 interview' },
    ],
  },
  {
    year: 1990, lane: 'exhibitions',
    coords: [29.76, -95.37], placeName: 'Houston · CAMH',
    title: 'International Pinhole Photography · Contemporary Arts Museum, Houston',
    body: '<p>Group show at CAMH, Houston (30 June – 9 September 1990). The first major US-museum survey of pinhole photography, and one of the few times Stroobant is shown stateside.</p>',
    img: '/img/4d8686-contemporary-arts-museum-houston-exterior.webp',
    imgAlt: 'Contemporary Arts Museum Houston',
    imgCap: 'The Contemporary Arts Museum Houston — Gunnar Birkerts\'s stainless-steel parallelogram. One of the few US venues to show him.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 1991, endYear: 1992, lane: 'works',
    coords: [44.08, 10.10], placeName: 'Carrara workshop',
    title: 'More Max Bill commissions · executed with El Rayes',
    body: '<p>Two more sculptures by Max Bill are executed at the Carrara workshop with co-carver <strong>El Rayes</strong> (identity not confirmed by independent sources). The Museo dei Bozzetti catalogue record for STYLITHOS is the source. Five years after Kontinuität, Bill is back at the same workshop.</p>',
    img: '/img/e803a2-com-l19-0188-0203a.webp',
    imgAlt: 'Max Bill',
    imgCap: 'Max Bill (1908–1994).<br /><span class="muted">ETH-Bibliothek Zürich · CC BY-SA</span>',
  },
  {
    year: 1994, lane: 'life',
    coords: [44.41, 8.93], placeName: 'Genoa, Italy',
    title: 'Meets Jean Gimpel and Tiziano Mannoni',
    body: '<p>Meets the medieval-technology historian <a href="https://en.wikipedia.org/wiki/Jean_Gimpel" target="_blank" rel="noopener"><strong>Jean Gimpel</strong></a> (author of <em>The Medieval Machine</em>) and the Genoese archaeologist <strong>Tiziano Mannoni</strong>. Both relationships feed his later participation in the <a href="https://en.wikipedia.org/wiki/Villard_de_Honnecourt" target="_blank" rel="noopener"><strong>Villard de Honnecourt</strong></a> exhibitions of 2002–2004 — a medieval architect whose c. 1230 notebook on stonecutting, geometry and building technique is preserved at the <a href="https://en.wikipedia.org/wiki/Biblioth%C3%A8que_nationale_de_France" target="_blank" rel="noopener">Bibliothèque nationale de France</a>. <a class="cite" href="https://www.iscum.it/villard-de-honnecourt-e-i-modi-di-costruire/" target="_blank" rel="noopener">ISCUM</a></p>',
    img: '/img/f72819-villard-de-honnecourt.webp',
    imgAlt: 'A page from Villard de Honnecourt\'s portfolio',
    imgCap: 'A page from the portfolio of Villard de Honnecourt, c. 1230. The Drei-Zirkelmethode is from the same medieval lineage.<br /><span class="muted">Bibliothèque nationale de France · Public Domain</span>',
    imgFit: 'contain',
  },
  {
    year: 1995, lane: 'exhibitions',
    coords: [50.85, 4.35], placeName: 'Brussels, Belgium',
    title: "Bureaux d'artistes · Espace Forma, Brussels",
    body: '<p>Group show of artists\' studios — Speed Rack International &amp; Stow International — at Espace Forma, Brussels.</p>',
  },
  {
    year: 1996, lane: 'exhibitions',
    coords: [43.68, 4.63], placeName: 'Arles, France',
    title: 'Rencontres Internationales de la Photographie · Arles',
    img: '/img/0d6605-francearlesarenes-07-2010.webp',
    imgAlt: 'Roman amphitheatre at Arles',
    imgCap: 'The Roman amphitheatre at Arles — host city of the canonical French photography festival.<br /><span class="muted">Wikimedia Commons · public domain</span>',
    body: '<p>The canonical French photography festival in Arles — group show <em>"La Corne de la Licorne"</em>. One of the more visible international photography stages of the period.</p>',
  },
  {
    year: 1998, lane: 'life',
    coords: [44.0835, 10.1156], placeName: 'Miseglia, Carrara',
    title: 'Death of his wife Mona Johnson',
    body: '<p>Mona dies after twenty-five years of marriage. Stroobant continues to live and work in the same Miseglia house alone with his teenage children — Ish-maël (26) and Mascha (20).</p>',
  },
  {
    year: 1999, endYear: 2004, lane: 'works',
    coords: [44.10, 10.10], placeName: 'Torano, Carrara',
    title: 'Co-curator · Torano Notte e Giorno',
    body: '<p>For six years he is co-curator of the annual summer event <em>Torano Notte e Giorno</em> in Torano, a hamlet adjacent to Miseglia and the home of the SGF sculpture workshop (Silvio Santini, Paolo Grassi, Mario Fruendi). His first institutional / curatorial role beyond his own studio.</p>',
    img: '/img/74d84c-carrara-torano-rampa-di-accesso-al-paese.webp',
    imgAlt: 'Access ramp to Torano, Carrara',
    imgCap: 'The ramp up to Torano — the frazione adjacent to Miseglia where he ran the summer event for six years.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    fact: { num: '6', unit: 'yrs', label: 'Co-curating Torano Notte e Giorno' },
  },
  {
    year: 2001, lane: 'works', hero: true,
    coords: [41.3768, 2.1175], placeName: 'Universitat de Barcelona',
    title: 'Ferrer i Guàrdia · bas-relief for the Universitat de Barcelona',
    body: '<p>On 16 October 2001 a marble bas-relief is installed at the entrance of the Faculty of Business Sciences at the <a href="https://en.wikipedia.org/wiki/University_of_Barcelona" target="_blank" rel="noopener">Universitat de Barcelona</a>. It is the work of <strong>seven Fine Arts students</strong> from the Carrara Academy, directed by Stroobant alongside the Carrara typographer <strong>Alfonso Nicolazzi</strong>. The drawing it reproduces is by <a href="https://en.wikipedia.org/wiki/Flavio_Costantini" target="_blank" rel="noopener"><strong>Flavio Costantini</strong></a>, depicting the execution of <a href="https://en.wikipedia.org/wiki/Francisco_Ferrer" target="_blank" rel="noopener"><strong>Francisco Ferrer i Guàrdia</strong></a> — anarchist pedagogue, founder of the <a href="https://en.wikipedia.org/wiki/Modern_School_(Spain)" target="_blank" rel="noopener">Modern School</a> — by firing squad at <a href="https://en.wikipedia.org/wiki/Montju%C3%AFc_Castle" target="_blank" rel="noopener">Montjuïc Castle</a> on 13 October 1909.</p><p>The commission came from the <a href="https://en.wikipedia.org/wiki/Italian_Anarchist_Federation" target="_blank" rel="noopener"><strong>Federazione Anarchica Italiana</strong></a>, founded in Carrara on 19 September 1945. <a class="cite" href="https://statues.vanderkrogt.net/object.php?webpage=ST&record=itto131" target="_blank" rel="noopener">Statues — Van der Krogt</a></p>',
    img: '/img/a520f6-francisco-ferrer-guardia.webp',
    imgAlt: 'Francisco Ferrer i Guàrdia',
    imgCap: 'Francisco Ferrer i Guàrdia (1859–1909).<br /><span class="muted">Wikimedia Commons · public domain.</span>',
    imgFit: 'top',
    fact: { num: '7', label: 'Carrara students carved it' },
    links: [
      { url: 'https://en.wikipedia.org/wiki/Francisco_Ferrer', label: 'Francisco Ferrer — Wikipedia' },
      { url: 'https://statues.vanderkrogt.net/object.php?webpage=ST&record=itto131', label: 'Statues — Van der Krogt record' },
    ],
  },
  {
    year: 2001, lane: 'exhibitions',
    coords: [33.83, -106.65], placeName: 'Pinhole Resource · New Mexico',
    title: 'Pinhole Journal Vol. 17 №1 · single-artist issue',
    body: '<p>The American journal <em>Pinhole Journal</em> (Eric Renner &amp; Nancy Spencer, Pinhole Resource, 1985 – 2006) devotes its whole Volume 17 №1 issue to Stroobant — the canonical single-artist recognition of his photographic work. Paolo Gioli, the other European pinhole pioneer, got the same treatment in Vol. 12 №2.</p>',
    img: '/img/bc1ada-when-the-sun-draws-in-the-sky-potw2311a.webp',
    imgAlt: 'ESO solargraph "When the Sun draws in the sky"',
    imgCap: 'A six-month solargraph from ESO\'s APEX site, Chile — <em>"When the Sun draws in the Sky"</em>. The technique Stroobant pioneered for art is now also used in astronomy.<br /><span class="muted">ESO / R. Bergerot · Wikimedia · CC BY 4.0</span>',
    links: [{ url: 'https://pinholeresource.com/pinhole-journal-55-back-issues/', label: 'Pinhole Resource back issues' }],
  },
  {
    year: 2002, lane: 'exhibitions',
    coords: [43.32, 11.33], placeName: 'Santa Maria della Scala · Siena',
    title: 'Senza Obiettivo · Santa Maria della Scala, Siena',
    body: '<p>Italy\'s first international pinhole-photography exhibition (XI Visionaria Festival), curated by <strong>Mauro Tozzi</strong> and <strong>Alessandro Mlach</strong>. The venue is Santa Maria della Scala, the medieval hospital on Siena\'s cathedral square. Stroobant builds a <strong>3 × 3 m camera obscura with variable aperture</strong> for the show and contributes an essay to the catalogue: <em>"Un contributo allo studio dell\'immagine per diffrazione"</em>.</p>',
    img: '/img/652c58-facade-santa-maria-della-scala.webp',
    imgAlt: 'Santa Maria della Scala, Siena',
    imgCap: 'Santa Maria della Scala, Siena.<br /><span class="muted">Wikimedia Commons · CC BY-SA 4.0</span>',
  },
  {
    year: 2002, lane: 'works',
    coords: [44.41, 8.93], placeName: 'Genoa · Sant\'Agostino',
    title: 'Meets Roland Bechmann · Villard de Honnecourt didactic project',
    body: '<p>Meets <strong>Roland Bechmann</strong>, the French scholar of the medieval architect <em>Villard de Honnecourt</em>. The two collaborate on didactic exhibitions about Villard\'s c. 1230 notebook — first at the Liceo Artistico di Carrara (2004), then at Museo Sant\'Agostino, Genoa (29 October 2004), within the Festival della Scienza. Stroobant\'s interest in the pre-industrial labour traditions of stonecutting now has a documented medieval anchor.</p>',
    img: '/img/2822c8-convento-di-sant-agostino.webp',
    imgAlt: 'Convento di Sant\'Agostino, Genoa',
    imgCap: 'Convento di Sant\'Agostino, Genoa — the medieval cloister that became the Museo Sant\'Agostino, host of the 2004 Villard didactic show.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    links: [{ url: 'https://www.iscum.it/villard-de-honnecourt-e-i-modi-di-costruire/', label: 'ISCUM — Villard de Honnecourt project' }],
  },
  {
    year: 2006, lane: 'works',
    coords: [44.08, 10.10], placeName: 'Carrara, Italy',
    title: 'Non son l\'uno per cento · Antonio Morabito\'s documentary',
    body: '<p>Stroobant is one of five interview subjects in <strong>Antonio Morabito</strong>\'s 75-minute documentary <em>"<a href="https://unopercento.wordpress.com/" target="_blank" rel="noopener">Non son l\'uno per cento — anarchici a Carrara</a>"</em>: alongside the typographer Alfonso Nicolazzi (his partner on the Ferrer monument), the professor Gigi Di Lembo, the historian Massimiliano Giorgi, and the activist Donato Landini. Two years of filming, forty hours of footage, one digital camera. Subjects: 1894 (the Carrara uprising), 1900 (Bresci killing King Umberto I), 1936 (the <a href="https://en.wikipedia.org/wiki/Spanish_Revolution_of_1936" target="_blank" rel="noopener">Spanish Revolution</a>), 1944 (the women\'s rebellion), 1945 (the FAI in Carrara), 1968 (the IAF-IFA international), and where the movement is now. <strong>The film embedded here is the canonical visual record of Stroobant\'s politics.</strong> <a class="cite" href="https://unopercento.wordpress.com/" target="_blank" rel="noopener">project blog</a></p>',
    videoId: 'morabito-full',
    links: [{ url: 'https://unopercento.wordpress.com/', label: 'Project blog' }],
  },
  {
    year: 2007, lane: 'exhibitions',
    title: 'Artempo · Palazzo Fortuny, Venice · the Vervoordt return',
    body: '<p><a href="https://en.wikipedia.org/wiki/Axel_Vervoordt" target="_blank" rel="noopener">Axel Vervoordt</a> curates <em>"Artempo: Where Time Becomes Art"</em> at <a href="https://en.wikipedia.org/wiki/Palazzo_Fortuny" target="_blank" rel="noopener">Palazzo Fortuny</a> in Venice. Stroobant is in the show. After almost two decades of near-silence in the gallery world, this is his comeback. Five more Fortuny shows will follow. <a class="cite" href="https://www.axel-vervoordt.com/gallery/artists/dominique-stroobant" target="_blank" rel="noopener">Vervoordt</a></p>',
    img: '/img/9234b3-palazzo-fortuny-gi-pesaro-orfei.webp',
    imgAlt: 'Palazzo Fortuny, Venice',
    imgCap: 'Palazzo Fortuny (Pesaro Orfei), Venice. The home of Vervoordt\'s curatorial cycle from 2007 to 2017.<br /><span class="muted">Wikimedia Commons</span>',
  },
  {
    year: 2007, lane: 'exhibitions',
    coords: [47.21, 7.79], placeName: 'Langenthal, Switzerland',
    title: 'Aufbruch ins Material · Kunsthaus Langenthal, Switzerland',
    body: '<p>Group show <em>"Aufbruch ins Material"</em> at the Sammlung Liechti / Kunsthaus Langenthal.</p>',
    img: '/img/f4b523-kunsthaus-langenthal-2008.webp',
    imgAlt: 'Kunsthaus Langenthal, Switzerland',
    imgCap: 'Kunsthaus Langenthal — the Bern-canton contemporary-art venue.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 2009, lane: 'works',
    coords: [44.0835, 10.1156], placeName: 'Miseglia, Carrara',
    title: 'Schwarz Darstellen · Hommage to Jef Verheyen',
    body: '<p>An homage to his late friend <a href="https://en.wikipedia.org/wiki/Jef_Verheyen" target="_blank" rel="noopener"><strong>Jef Verheyen</strong></a>, referencing a 1975 letter from Verheyen to Stroobant on <a href="https://en.wikipedia.org/wiki/Paul_Klee" target="_blank" rel="noopener">Paul Klee</a>\'s concept of <em>Schwarz darstellen</em> ("depicting black"). Verheyen was a key member of the <a href="https://en.wikipedia.org/wiki/Zero_(art)" target="_blank" rel="noopener">ZERO movement</a> (Düsseldorf-Milan-Antwerp axis) and Stroobant\'s closest Belgian collaborator from the 1970s on; he died in 1984.</p>',
    img: '/img/86bb23-paul-klee-gespenst-eines-genies-ghost-of-a-geniu.webp',
    imgAlt: 'Paul Klee, Ghost of a Genius',
    imgCap: 'Paul Klee, <em>Gespenst eines Genies</em>. Klee\'s <em>Schwarz darstellen</em> — "depicting black" — is the concept the 1975 Verheyen letter passes to Stroobant.<br /><span class="muted">Wikimedia Commons · public domain</span>',
  },
  {
    year: 2009, lane: 'exhibitions',
    coords: [45.44, 12.33], placeName: 'Palazzo Fortuny · Venice',
    title: 'In-finitum · Palazzo Fortuny, Venice',
    body: '<p>Second of six Vervoordt-era Palazzo Fortuny group shows. The cycle\'s subject — time as material — is one Stroobant has been pursuing in stone and in pinhole photography since the 1970s, so the curatorial fit is unusually direct.</p>',
    img: '/img/0a7c00-interno-museo-fortuny-venezia-0.webp',
    imgAlt: 'Inside the Palazzo Fortuny museum, Venice',
    imgCap: 'Inside the Palazzo Fortuny — the Mariano Fortuny rooms that Vervoordt installs his cycle into.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 2010, lane: 'works',
    coords: [44.0871, 10.1262], placeName: 'Miseglia · Via Fantiscritti 11',
    title: 'Studio is renamed STYLITHOS',
    body: '<p>The workshop, registered since 1975 under the name <em>Stroobant Dominique</em>, is renamed <strong>Stylithos</strong> as of 2010. Same address (Via Fantiscritti 11), same crew, same medieval-lodge organisation. The Museo dei Bozzetti record dates from this naming. Greek <em>lithos</em> — stone.</p>',
  },
  {
    year: 2011, lane: 'exhibitions',
    coords: [45.44, 12.33], placeName: 'Palazzo Fortuny · Venice',
    title: 'TRA. Edge of Becoming · Palazzo Fortuny',
    body: '<p>Third Vervoordt Palazzo Fortuny show — <em>"TRA. Edge of Becoming"</em>.</p>',
    img: '/img/a3bca3-venezia-palazzo-fortuny-interno-2017-01.webp',
    imgAlt: 'Palazzo Fortuny interior, Venice',
    imgCap: 'Palazzo Fortuny interior — third show in the Vervoordt cycle.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 2012, lane: 'exhibitions',
    coords: [51.22, 4.40], placeName: 'Vervoordt Gallery · Antwerp',
    title: 'Tempi e Fili — Seil und Zeit · Axel Vervoordt Gallery, Antwerp',
    body: '<p>Solo show at the Vervoordt gallery in Antwerp (26 January – 10 March 2012). Catalogue essay by Dirk Pörschmann: <em>"Finding the Source: Dominique Stroobant and the Joy of Beginnings"</em>. The gallery sits at <strong>Kanaal</strong>, the old Meeus distillery site in Wijnegem that Vervoordt has been converting since the 1990s.</p>',
    img: '/img/a60066-oude-site-stokerij-meeus.webp',
    imgAlt: 'The old Meeùs distillery — site of Kanaal / Axel Vervoordt Gallery in Wijnegem',
    imgCap: 'The former Meeùs distillery, Wijnegem — the industrial brick complex Axel Vervoordt has turned into <em>Kanaal</em>, the gallery&apos;s home.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    quote: { text: 'If art is not a necessity, you don\'t need any art. It must want to get out of you.', emphasis: 'necessity', source: 'Stroobant, Tempi e Fili catalogue, 2012' },
  },
  {
    year: 2013, lane: 'exhibitions',
    coords: [43.77, 11.26], placeName: 'Florence, Italy',
    title: 'Visivi · round table in Florence',
    body: '<p>Moderates a round table <em>"Visivi. La fotografia attraverso i linguaggi contemporanei"</em> in Florence. Two sources name two venues: the Museo Galileo (per the Vervoordt biographical résumé) and the Fondazione Alinari (per Italian Wikipedia). Possibly two separate events.</p>',
    img: '/img/033782-galilei-s-older-telescope-eyepiece-part-museo-ga.webp',
    imgAlt: 'Galileo\'s telescope eyepiece, Museo Galileo, Florence',
    imgCap: 'Galileo\'s telescope eyepiece, Museo Galileo, Florence — the lens-history institution that hosted the round table.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 2015, lane: 'exhibitions',
    coords: [51.22, 4.40], placeName: 'Vervoordt Gallery · Antwerp',
    title: 'Dominique Stroobant · solo · Axel Vervoordt, Antwerp · monograph published',
    body: '<p>Second Vervoordt-Antwerp solo (15 October – 29 November 2015). Same year, the AsaMER / MER. Paper Kunsthalle monograph is published — 300 pages, hardcover, English, with the Dirk Pörschmann essay. ISBN 978-94-91775-82-6, €45.</p>',
  },
  {
    year: 2015, lane: 'works',
    coords: [51.22, 4.40], placeName: 'AsaMER · Antwerp',
    title: 'Monograph published · 300 pp · AsaMER / MER. Paper Kunsthalle',
    body: '<p>The first comprehensive monograph. 300 pages, hardcover, English, with essay <em>"Finding the Source: Dominique Stroobant and the Joy of Beginnings"</em> by Dirk Pörschmann. Released alongside his second Vervoordt-Antwerp solo show.</p>',
    quote: { text: 'Art doesn\'t have an essence. For me, the abstract term "art" is a dream or drive. A good work for me is like a new word. I struggle over stones and words because what must come is and remains yet-to-be imagined.', emphasis: 'yet-to-be imagined', source: 'Stroobant, Axel Vervoordt artist page' },
  },
  {
    year: 2015, lane: 'exhibitions',
    coords: [45.44, 12.33], placeName: 'Palazzo Fortuny · Venice',
    title: 'PROPORTIO · Palazzo Fortuny',
    body: '<p>Fourth Vervoordt Palazzo Fortuny show.</p>',
    img: '/img/556269-venezia-palazzo-fortuny-interno-2017-02.webp',
    imgAlt: 'Palazzo Fortuny interior',
    imgCap: 'Palazzo Fortuny — fourth Vervoordt show, on proportion as a Renaissance lineage.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 2016, lane: 'exhibitions',
    coords: [43.05, 10.89], placeName: 'Massa Marittima, Tuscany',
    title: 'Slow Photo Festival · Massa Marittima',
    img: '/img/51d072-massa-marittima-001.webp',
    imgAlt: 'Massa Marittima, Tuscany',
    imgCap: 'Massa Marittima — the Tuscan town that hosts the Slow Photo festival.<br /><span class="muted">Wikimedia Commons · CC BY-SA 4.0</span>',
    body: '<p>Participates in the Slow Photo festival in Massa Marittima, a small Tuscan town. The "slow photo" framing fits his six-month exposures explicitly.</p>',
  },
  {
    year: 2017, lane: 'exhibitions',
    coords: [45.44, 12.33], placeName: 'Palazzo Fortuny · Venice',
    title: 'INTUITION · Palazzo Fortuny',
    body: '<p>Fifth Vervoordt Palazzo Fortuny show (a sixth — <em>Artempo</em> 2007 — opened the run a decade earlier). Closes a ten-year cycle.</p>',
    img: '/img/acdbf8-venezia-palazzo-fortuny-07.webp',
    imgAlt: 'Palazzo Fortuny façade detail with Pesaro escutcheon',
    imgCap: 'Palazzo Fortuny façade detail with the Pesaro family escutcheon — the same building closing a ten-year Vervoordt cycle.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
  },
  {
    year: 2020, lane: 'exhibitions',
    coords: [50.85, 4.35], placeName: 'Art Brussels',
    title: 'La Sfera Schiacciata III · Art Brussels (Vervoordt booth)',
    body: '<p>Axel Vervoordt Gallery presents one of Stroobant\'s solo sculptures — <em>La Sfera Schiacciata III</em>, "the crushed sphere III" — at Art Brussels 2020. A granite sphere distorted by a single diamond-wire cut. The piece is shown below in Boris Vervoordt\'s booth tour. <strong>This is the answer to "what does Stroobant make beside Kontinuität": his own sculptures are mostly stone solids transformed by ruled-surface cuts.</strong></p>',
    img: '/img/c45c4e-tour-et-taxis-brussels-skyline.webp',
    imgAlt: 'Tour & Taxis, Brussels — the Art Brussels venue',
    imgCap: 'Tour &amp; Taxis, Brussels — the converted customs warehouses that host Art Brussels each year.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    videoId: 'vervoordt-sfera',
  },
  {
    year: 2026, lane: 'life',
    coords: [44.0835, 10.1156], placeName: 'Miseglia, Carrara',
    title: 'He is 79 · still in Miseglia',
    body: '<p>At the time of writing (May 2026) he is <strong>seventy-nine years old</strong>. He has lived in the same Miseglia house for <strong>fifty-six years</strong>. The Stylithos workshop is still at Via Fantiscritti 11. The Kontinuität monolith stands at Taunusanlage; the Marcinelle memorial stands at Bois du Cazier; the Ferrer bas-relief stands at the entrance of the Universitat de Barcelona; the Brialmontpark elephant still gets walked past by Berchem schoolchildren.</p><p>No-one carved a plaque saying who made any of them.</p>',
    img: '/img/5fb067-2002-dominique-stroobant.webp',
    imgAlt: 'Dominique Stroobant in his workshop',
    imgCap: 'Dominique Stroobant, photographed in his Miseglia workshop in 2002.<br /><span class="muted">Wikimedia Commons · CC BY-SA</span>',
    imgFit: 'top',
  },
];

function formatYear(e: Entry): string {
  if (e.endYear) return `${e.year} — ${e.endYear}`;
  return String(e.year);
}
function isRich(e: Entry): boolean {
  // Context entries always use the compact flipped layout
  if (e.kind === 'context') return false;
  // Explicit override wins
  if (e.display === 'rich') return true;
  if (e.display === 'compact') return false;
  // No body-length heuristic — rich only if there is real media to render
  return !!(e.img || e.videoId || e.fact || (e.embeds && e.embeds.length > 0) || e.quote);
}
function hasSide(e: Entry): boolean {
  return !!(e.img || e.videoId || e.fact);
}
function cleanBody(body: string): string {
  // Strip <p> wrappers but keep inline strong/em — used for compact rendering
  return body.replace(/<\/?p>/g, ' ').trim();
}
function highlightedQuote(text: string, emphasis?: string): string {
  if (!emphasis) return text;
  const safe = text.replace(/&/g, '&amp;').replace(/</g, '&lt;');
  const target = emphasis.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return safe.replace(new RegExp(`(${target})`, 'i'), '<span class="accent">$1</span>');
}

const filteredEntries = computed(() => {
  return entries.filter((e) => e.year >= props.from && e.year <= props.to);
});

// rAF-throttled scroll handler. Picks the entry whose top edge is closest to
// the "focus line" (30% from the top of the viewport). Hysteresis: we only flip
// to a new entry if its top is strictly closer to the focus line than the current
// active entry — prevents oscillation when two entries straddle the line.
let scrollRaf = 0;
function pickActive() {
  scrollRaf = 0;
  const focusY = window.innerHeight * 0.35;
  let bestIdx = activeIdx.value;
  let bestDist = Infinity;
  // Re-measure the current active first; we beat it only by a meaningful margin.
  const refs = entryRefs.value;
  for (let i = 0; i < refs.length; i++) {
    const el = refs[i];
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    // Only consider entries that have any pixels above the focus line
    if (rect.bottom < 0 || rect.top > window.innerHeight) continue;
    const d = Math.abs(rect.top - focusY);
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  }
  if (bestIdx === activeIdx.value || bestIdx < 0) return;
  const el = refs[bestIdx];
  if (!el) return;
  activeIdx.value = bestIdx;
  const coords = el.dataset.coords;
  if (coords) {
    const [lat, lon] = coords.split(',').map(Number);
    activeCoords.value = [lat, lon];
  } else {
    activeCoords.value = null;
  }
  activeYear.value = el.dataset.year || null;
  activePlace.value = el.dataset.place || null;
}
function onScroll() {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(pickActive);
}

onMounted(async () => {
  await nextTick();
  // Initial pick
  pickActive();
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  // The floating map is visible only while the timeline section is on screen
  if (typeof IntersectionObserver !== 'undefined' && sectionRoot.value) {
    visibilityIo = new IntersectionObserver(
      (records) => { mapVisible.value = records[0]?.isIntersecting ?? false; },
      { rootMargin: '0px', threshold: 0 },
    );
    visibilityIo.observe(sectionRoot.value);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onScroll);
  window.removeEventListener('resize', onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
  visibilityIo?.disconnect();
});
</script>

<style scoped>
.tl {
  background: var(--paper);
  padding: clamp(4rem, 9vh, 7rem) 0;
}
.tl__head {
  max-width: var(--max-w);
  margin: 0 auto 2rem;
  padding: 0 var(--gutter);
}
.tl__h {
  font-family: var(--font-display);
  font-size: clamp(2.4rem, 5vw, 4.4rem);
  font-weight: 600;
  line-height: 1.04;
  margin: 0 0 1rem;
  letter-spacing: -0.014em;
  max-width: 22ch;
}
.tl__sub {
  font-size: 1.18rem;
  line-height: 1.55;
  color: var(--slate-soft);
  max-width: 64ch;
  margin: 0;
}

.tle {
  border-top: 1px solid var(--vein);
  background: var(--paper);
}
.tle--rich {
  padding: clamp(2.4rem, 5vh, 4rem) var(--gutter);
}
.tle--rich.tle--alt { background: var(--paper-deep); }
.tle__inner { max-width: var(--max-w); margin: 0 auto; }

/* Compact row — condensed but readable; never anaemic */
.tle--compact {
  padding: 1.3rem var(--gutter);
}
.tle--compact__inner {
  max-width: var(--max-w);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 2rem;
  align-items: start;
}
.tle--compact__year {
  font-family: var(--font-numeral);
  font-weight: 400;
  color: var(--accent-cursive);
  font-size: clamp(2.6rem, 4.6vw, 4.4rem);
  letter-spacing: 0;
  line-height: 1;
  padding-top: 0.05rem;
  font-feature-settings: 'onum' on;
}
.tle--compact__body { display: grid; gap: 0.35rem; }
.tle--compact__title {
  font-family: var(--font-body);
  font-size: clamp(1.1rem, 1.45vw, 1.4rem);
  font-weight: 700;
  margin: 0;
  color: #000;
  line-height: 1.33;
  letter-spacing: 0;
}
.tle--compact__txt {
  font-family: var(--font-display);
  font-size: clamp(1.1rem, 1.45vw, 1.4rem);
  line-height: 1.33;
  color: #000;
  margin: 0.25rem 0 0;
  font-weight: 400;
  font-variant-numeric: oldstyle-nums proportional-nums;
  letter-spacing: 0.01em;
}
.tle--compact__txt :deep(strong) { color: var(--slate); font-weight: 600; }
.tle--compact__txt :deep(em) { font-style: italic; }
.tle--compact__links { margin-top: 0.3rem; display: flex; flex-wrap: wrap; gap: 0.3rem 1.4rem; font-size: 0.9rem; }
.tle--compact__links a { color: var(--accent-deep); }

/* Context entries — Carrara anarchism, pre-Stroobant background.
   Layout is FLIPPED: year is on the right, body is on the left.
   Plus a permanent "CONTEXT" bar above the body. */
.tle--context {
  background: rgba(27, 27, 31, 0.025);
  border-right: 3px solid var(--granite-soft);
  border-left: none;
}
.tle--context .tle--compact__inner {
  grid-template-columns: 1fr 220px;
}
.tle--context .tle--compact__year {
  order: 2;
  text-align: right;
  color: #555;
  font-weight: 400;
  opacity: 0.5;
  font-family: var(--font-numeral);
  font-size: clamp(2.6rem, 4.6vw, 4.6rem);
  line-height: 1;
}
.tle--context .tle--compact__body { order: 1; }
.tle--context .tle--compact__title {
  color: #000;
  font-style: normal;
  font-weight: 700;
}
.tle--context .tle--compact__body::before {
  content: 'CONTEXT';
  display: block;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  color: #F1ECE4;
  background: rgba(85, 85, 85, 0.5);
  padding: 0.34rem 0.7rem;
  border-radius: 4px;
  margin-bottom: 0.8rem;
  font-weight: 400;
  width: 100%;
  text-transform: uppercase;
  box-sizing: border-box;
}
@media (max-width: 760px) {
  .tle--context .tle--compact__inner { grid-template-columns: 1fr; }
  .tle--context .tle--compact__year { order: 0; text-align: left; }
}

@media (max-width: 760px) {
  .tle--compact__inner { grid-template-columns: 1fr; gap: 0.4rem; }
}

.tle__head {
  padding-bottom: 0.4rem;
  border-bottom: 1px solid var(--vein);
  margin-bottom: 0.9rem;
}
.tle__year {
  font-family: var(--font-numeral);
  font-size: clamp(3rem, 5.5vw, 5.4rem);
  font-weight: 400;
  color: var(--accent-cursive);
  line-height: 1;
  letter-spacing: 0;
  font-feature-settings: 'onum' on;
}
.tle__title {
  font-family: var(--font-body);
  font-size: clamp(1.5rem, 2.2vw, 2.2rem);
  font-weight: 500;
  line-height: 1.11;
  margin: 0 0 1.2rem;
  color: #000;
  letter-spacing: 0;
}

.tle__layout {
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 2.4rem;
  align-items: start;
}
.tle__layout.no-side { grid-template-columns: 1fr; }
.tle__side { display: grid; gap: 1.4rem; align-content: start; }
.tle__body {
  font-family: var(--font-display);
  font-size: clamp(1.15rem, 1.5vw, 1.45rem);
  line-height: 1.33;
  color: #000;
  font-weight: 400;
  font-variant-numeric: oldstyle-nums proportional-nums;
  letter-spacing: 0.01em;
}
.tle__body :deep(p) { margin: 0 0 1.2em; }
.tle__body :deep(p:last-child) { margin-bottom: 0; }
.tle__body :deep(strong) { color: #000; font-weight: 700; }
.tle__body :deep(em) { font-style: italic; }
/* Inline link inside body prose: subtle underline that brightens on hover */
.tle__body :deep(a:not(.cite)),
.tle--compact__txt :deep(a:not(.cite)) {
  color: var(--slate);
  text-decoration: underline;
  text-decoration-color: var(--accent-soft);
  text-decoration-thickness: 1.5px;
  text-underline-offset: 2px;
  transition: color 0.15s ease, text-decoration-color 0.15s ease;
}
.tle__body :deep(a:not(.cite):hover),
.tle--compact__txt :deep(a:not(.cite):hover) {
  color: var(--accent-deep);
  text-decoration-color: var(--accent);
}
/* Inline source citation marker — Space Mono SRC pill */
.tle__body :deep(a.cite),
.tle--compact__txt :deep(a.cite) {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 0.62em;
  font-weight: 400;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--accent-deep-2);
  background: rgba(194, 90, 58, 0.08);
  padding: 0.18em 0.5em;
  margin: 0 0.1em 0 0.18em;
  border-radius: 3px;
  text-decoration: none;
  vertical-align: 0.18em;
  border: 1px solid rgba(194, 90, 58, 0.22);
}
.tle__body :deep(a.cite:hover),
.tle--compact__txt :deep(a.cite:hover) {
  background: var(--accent);
  color: var(--paper);
  border-color: var(--accent-deep);
}

.tle__fig { margin: 0; display: grid; gap: 0.6rem; }
.tle__fig img {
  width: 100%;
  height: auto;
  aspect-ratio: 4 / 3;
  border-radius: 3px;
}
.tle__fig-img--cover { object-fit: cover; }
.tle__fig-img--top { object-fit: cover; object-position: 50% 8%; }
.tle__fig-img--contain {
  object-fit: contain;
  background: var(--paper-deep);
}
.tle__fig figcaption {
  font-family: var(--font-body);
  font-size: 0.95rem;
  line-height: 1.5;
  color: #000;
  opacity: 0.7;
  font-weight: 400;
}
.tle__fig figcaption :deep(.muted) { font-size: 0.78rem; letter-spacing: 0.05em; }

.tle__video { display: grid; gap: 0.6rem; }
.tle__video :deep(.video-fig) { margin: 0; }

.tle__embed { margin-top: 1.4rem; }

.tle__quote {
  margin: 1.6rem 0 0;
  padding: 1.4rem 1.8rem;
  border-left: 3px solid var(--accent-cursive);
  background: rgba(209, 82, 48, 0.06);
}
.tle__quote blockquote {
  margin: 0;
  font-family: var(--font-body);
  font-style: italic;
  font-size: clamp(1.2rem, 1.55vw, 1.5rem);
  line-height: 1.33;
  color: rgba(0, 0, 0, 0.8);
  font-weight: 700;
}
.tle__quote blockquote :deep(em) {
  color: var(--accent-cursive);
  font-style: italic;
}
.tle__quote figcaption {
  margin-top: 0.7rem;
  font-family: var(--font-body);
  font-size: 1.05rem;
  letter-spacing: 0;
  text-transform: none;
  color: #000;
  opacity: 0.6;
  font-weight: 400;
  line-height: 1.55;
}

/* Fact is a marginalia inside the side column — sits under photo (or alone if no photo) */
.tle__fact {
  padding: 1rem 1.2rem;
  background: rgba(194, 90, 58, 0.05);
  border-left: 3px solid var(--accent);
  border-radius: 0 4px 4px 0;
  display: grid;
  gap: 0.4rem;
}
.tle__factnum {
  font-family: var(--font-body);
  font-size: clamp(2.2rem, 3.2vw, 2.75rem);
  color: var(--accent-cursive);
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0;
}
.tle__factunit {
  font-family: var(--font-body);
  font-size: 0.5em;
  margin-left: 0.06em;
  color: var(--accent-cursive);
  font-weight: 500;
}
.tle__factlabel {
  font-family: var(--font-body);
  font-size: clamp(1rem, 1.4vw, 1.4rem);
  letter-spacing: 0;
  text-transform: none;
  color: #000;
  line-height: 1.33;
  font-weight: 400;
}

.tle__links {
  margin-top: 1.4rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem 1.6rem;
  font-size: 0.96rem;
}
.tle__links a {
  color: var(--accent-deep);
  text-decoration: underline;
  text-decoration-color: var(--accent-soft);
  font-weight: 500;
}

@media (max-width: 900px) {
  .tle__layout { grid-template-columns: 1fr; }
}
</style>
